import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Calendar, ChevronRight, Sparkles, AlertTriangle, RotateCcw,
  CheckCircle2, Save, Clock, Truck, Gauge, User, X, Building2,
} from "lucide-react";
import SearchableSelect from "@/components/SearchableSelect";
import { fmtDate } from "@/lib/format";
import SignatureCanvas from "@/components/SignatureCanvas";

// =============================================================================
// AutoGenerator — Zeiterfassung & Lenkprotokoll Auto-Generator (Phase 1)
// =============================================================================
const MONTHS = [
  "Januar","Februar","März","April","Mai","Juni",
  "Juli","August","September","Oktober","November","Dezember",
];

export default function AutoGenerator() {
  const today = new Date();
  const [employees, setEmployees] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [ruleGroups, setRuleGroups] = useState([]);
  const [departments, setDepartments] = useState([]);

  const [form, setForm] = useState({
    department_id: "",      // NEW: filters the driver dropdown only
    employee_id: "",
    vehicle_id: "",
    rule_group_id: "",
    year: today.getFullYear(),
    month: today.getMonth() + 1,
    start_km: "",
    overtime_target_hours_min: 0,
    overtime_target_hours_max: 0,
    include_saturdays: false,
    show_inactive: false,   // NEW: also show abgemeldet drivers
  });

  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const [committing, setCommitting] = useState(false);
  const [sigModalOpen, setSigModalOpen] = useState(false);
  const [sigDataUrl, setSigDataUrl] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const [emp, veh, rg, dep] = await Promise.all([
          api.get("/employees"),
          api.get("/vehicles"),
          api.get("/auto-gen/rule-groups"),
          api.get("/departments"),
        ]);
        setEmployees((emp.data || []).filter(e => e.role === "fahrer"));
        setVehicles((veh.data || []).filter(v => v.status === "aktiv"));
        setRuleGroups(rg.data?.groups || []);
        setDepartments(dep.data || []);
      } catch (e) {
        toast.error("Daten konnten nicht geladen werden");
      }
    })();
  }, []);

  function f(k, v) {
    setForm(prev => {
      const next = { ...prev, [k]: v };
      // Reset selected driver when department filter changes
      if (k === "department_id") next.employee_id = "";
      return next;
    });
  }

  async function generatePreview() {
    if (!form.employee_id || !form.rule_group_id) {
      toast.error("Fahrer und Regel-Gruppe sind Pflicht");
      return;
    }
    setBusy(true);
    setPreview(null);
    try {
      const payload = {
        ...form,
        start_km: form.start_km ? parseInt(form.start_km, 10) : null,
        overtime_target_hours_min: parseFloat(form.overtime_target_hours_min) || 0,
        overtime_target_hours_max: parseFloat(form.overtime_target_hours_max) || 0,
      };
      const { data } = await api.post("/auto-gen/preview", payload);
      setPreview(data);
      toast.success(`${data.summary.days_generated} Tage generiert`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Vorschau fehlgeschlagen");
    } finally { setBusy(false); }
  }

  async function regenerateDay(d) {
    try {
      const { data } = await api.post("/auto-gen/regenerate-day", {
        run_id: preview.draft.run_id, date: d.date,
      });
      const updated = { ...preview };
      updated.draft.days = updated.draft.days.map(x => x.date === d.date ? data.day : x);
      setPreview(updated);
      toast.success(`Tag ${d.date} neu generiert`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fehler");
    }
  }

  async function removeDay(d) {
    if (!window.confirm(`Tag ${d.date} aus dem Plan entfernen?`)) return;
    try {
      await api.patch("/auto-gen/day", {
        run_id: preview.draft.run_id, date: d.date,
        changes: { skipped: true, skip_reason: "Manuell entfernt" },
      });
      const updated = { ...preview };
      updated.draft.days = updated.draft.days.map(x => x.date === d.date
        ? { ...x, skipped: true, skip_reason: "Manuell entfernt" } : x);
      setPreview(updated);
    } catch { toast.error("Fehler"); }
  }

  async function commit() {
    if (!preview) return;
    // Open signature modal first — admin signs once on behalf of the driver
    // and the same signature is applied to every generated day.
    setSigDataUrl("");
    setSigModalOpen(true);
  }

  async function commitWithSignature() {
    if (!preview) return;
    setCommitting(true);
    try {
      const { data } = await api.post("/auto-gen/commit", {
        run_id: preview.draft.run_id,
        signature_data_url: sigDataUrl || "",
      });
      toast.success(`${data.created} neu · ${data.updated} überschrieben · ${data.skipped} übersprungen`);
      setPreview(null);
      setSigModalOpen(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Speichern fehlgeschlagen");
    } finally { setCommitting(false); }
  }

  const yearOpts = useMemo(() => Array.from({ length: 5 }, (_, i) => today.getFullYear() - 1 + i), []);
  const empOpts = useMemo(() => {
    let list = [...employees];
    if (!form.show_inactive) {
      list = list.filter(e => e.status !== "abgemeldet" && e.status !== "inaktiv");
    }
    if (form.department_id) {
      list = list.filter(e => e.department_id === form.department_id);
    }
    return list.sort((a, b) => (a.last_name || "").localeCompare(b.last_name || ""));
  }, [employees, form.department_id, form.show_inactive]);
  const empOptions = useMemo(() => empOpts.map(e => ({
    value: e.id,
    label: `${e.last_name || ""}, ${e.first_name || ""}${e.status === "abgemeldet" || e.status === "inaktiv" ? `  ·  [${e.status}]` : ""}`,
    hint: e.personnel_number || e.email || "",
  })), [empOpts]);
  const vehOptions = useMemo(() => {
    return [...vehicles]
      .sort((a, b) => (a.license_plate || "").localeCompare(b.license_plate || ""))
      .map(v => ({
        value: v.id,
        label: `${v.license_plate || ""} · ${v.brand || v.make || ""} ${v.model || ""}`.trim(),
        hint: v.vin || "",
      }));
  }, [vehicles]);
  const deptCounts = useMemo(() => {
    const m = {};
    const pool = form.show_inactive
      ? employees
      : employees.filter(e => e.status !== "abgemeldet" && e.status !== "inaktiv");
    for (const e of pool) m[e.department_id || ""] = (m[e.department_id || ""] || 0) + 1;
    return m;
  }, [employees, form.show_inactive]);
  const totalShownEmployees = useMemo(() => {
    return form.show_inactive
      ? employees.length
      : employees.filter(e => e.status !== "abgemeldet" && e.status !== "inaktiv").length;
  }, [employees, form.show_inactive]);

  return (
    <div className="space-y-6" data-testid="auto-gen-page">
      <header className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full bg-amber-500/10 border border-amber-500/30 text-[10px] uppercase tracking-wider text-amber-300 font-semibold mb-2">
            <Sparkles className="w-3 h-3" /> Neue Engine · Phase 1
          </div>
          <h1 className="font-display text-3xl font-bold text-zinc-50">Zeiterfassung Auto-Generator</h1>
          <p className="text-sm text-zinc-400 mt-1 max-w-2xl">
            Generiert einen kompletten Monat Arbeitszeit + Lenkprotokoll in unter einer Minute.
            Wähle Fahrer, Regel-Gruppe (UPS / GLS / DPD / Custom) und Zielmonat — der Rest läuft automatisch.
          </p>
        </div>
      </header>

      {/* Setup form */}
      <section className="bg-zinc-900 border border-zinc-800 rounded-lg p-6" data-testid="auto-gen-form">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <Field label="Abteilung (Filter)" icon={Building2} testid="autogen-dept">
            <select className={INP} data-testid="autogen-dept-select"
              value={form.department_id} onChange={e => f("department_id", e.target.value)}>
              <option value="">Alle Abteilungen ({totalShownEmployees})</option>
              {departments.map(d => (
                <option key={d.id} value={d.id}>
                  {d.name} ({deptCounts[d.id] || 0})
                </option>
              ))}
            </select>
          </Field>

          <Field label="Fahrer" icon={User} testid="autogen-emp">
            <SearchableSelect
              testid="autogen-emp-select"
              value={form.employee_id}
              onChange={v => f("employee_id", v)}
              options={empOptions}
              placeholder={form.department_id ? `— wählen (${empOpts.length} Fahrer in Abteilung) —` : `— wählen (${empOpts.length}) —`}
              emptyText="Kein Fahrer gefunden"
            />
          </Field>

          <Field label="Monat" icon={Calendar}>
            <div className="grid grid-cols-2 gap-2">
              <select className={INP} data-testid="autogen-month"
                value={form.month} onChange={e => f("month", parseInt(e.target.value, 10))}>
                {MONTHS.map((m, i) => <option key={i+1} value={i+1}>{m}</option>)}
              </select>
              <select className={INP} data-testid="autogen-year"
                value={form.year} onChange={e => f("year", parseInt(e.target.value, 10))}>
                {yearOpts.map(y => <option key={y} value={y}>{y}</option>)}
              </select>
            </div>
          </Field>

          <Field label="Regel-Gruppe" icon={Sparkles}>
            <select className={INP} data-testid="autogen-rule-group"
              value={form.rule_group_id} onChange={e => f("rule_group_id", e.target.value)}>
              <option value="">— wählen —</option>
              {ruleGroups.map(r => (
                <option key={r.id} value={r.id}>{r.code} — {r.name}</option>
              ))}
            </select>
          </Field>

          <Field label="Fahrzeug" icon={Truck}>
            <SearchableSelect
              testid="autogen-vehicle"
              value={form.vehicle_id}
              onChange={v => f("vehicle_id", v)}
              options={vehOptions}
              placeholder="— auto (letztes Fahrzeug des Fahrers) —"
              emptyText="Kein Fahrzeug gefunden"
            />
          </Field>

          <Field label="Start-KM (optional)" icon={Gauge}>
            <input type="number" className={INP} placeholder="Auto: letzter End-KM"
              data-testid="autogen-start-km"
              value={form.start_km} onChange={e => f("start_km", e.target.value)}/>
          </Field>

          <Field label="Überstunden-Ziel (Stunden, Zufalls-Range)" icon={Clock}>
            <div className="grid grid-cols-2 gap-2">
              <input type="number" min="0" max="80" step="0.25" className={INP}
                placeholder="von"
                data-testid="autogen-overtime-min"
                value={form.overtime_target_hours_min}
                onChange={e => f("overtime_target_hours_min", e.target.value)}/>
              <input type="number" min="0" max="80" step="0.25" className={INP}
                placeholder="bis"
                data-testid="autogen-overtime-max"
                value={form.overtime_target_hours_max}
                onChange={e => f("overtime_target_hours_max", e.target.value)}/>
            </div>
            <div className="text-[10px] text-zinc-500 mt-1">Zufälliger Wert in 0,25h-Schritten. Beide gleich = exakter Wert.</div>
          </Field>
        </div>

        <div className="mt-4 flex flex-wrap gap-4">
          <label className="flex items-center gap-2 text-sm text-zinc-300 cursor-pointer">
            <input type="checkbox" data-testid="autogen-show-inactive"
              checked={form.show_inactive}
              onChange={e => f("show_inactive", e.target.checked)}
              className="w-4 h-4 rounded accent-zinc-500"/>
            Auch abgemeldete Fahrer anzeigen
          </label>
          <label className="flex items-center gap-2 text-sm text-zinc-300 cursor-pointer">
            <input type="checkbox" data-testid="autogen-saturdays"
              checked={form.include_saturdays}
              onChange={e => f("include_saturdays", e.target.checked)}
              className="w-4 h-4 rounded accent-amber-500"/>
            Samstage einbeziehen
          </label>
          <div className="text-xs text-zinc-400 flex items-center gap-2 px-2 py-1 rounded bg-amber-500/5 border border-amber-500/20" data-testid="autogen-overwrite-info">
            <AlertTriangle className="w-3.5 h-3.5 text-amber-400"/>
            Bestehende Einträge werden automatisch überschrieben
          </div>
        </div>

        <div className="mt-6 flex justify-end">
          <button onClick={generatePreview} disabled={busy}
            data-testid="autogen-preview-btn"
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-amber-600 hover:bg-amber-700 text-white rounded font-medium disabled:opacity-50">
            <Sparkles className="w-4 h-4"/>
            {busy ? "Generiere …" : "Monat automatisch erstellen"}
            <ChevronRight className="w-4 h-4"/>
          </button>
        </div>
      </section>

      {/* Preview */}
      {preview && (
        <PreviewSection
          preview={preview}
          onRegenDay={regenerateDay}
          onRemoveDay={removeDay}
          onCommit={commit}
          committing={committing}
        />
      )}

      {/* Signature modal — admin signs once for the driver,
          the same signature gets applied to every committed day. */}
      {sigModalOpen && preview && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" data-testid="autogen-signature-modal">
          <div className="bg-zinc-900 border border-zinc-800 rounded-lg w-full max-w-lg">
            <header className="px-5 py-3 border-b border-zinc-800 flex items-center justify-between">
              <div>
                <div className="text-[10px] uppercase tracking-wider text-amber-300 font-semibold">Vor dem Speichern</div>
                <h3 className="font-display text-zinc-50 font-semibold mt-0.5">Unterschrift für Fahrer</h3>
              </div>
              <button onClick={() => setSigModalOpen(false)} className="text-zinc-500 hover:text-zinc-200">
                <X className="w-4 h-4" />
              </button>
            </header>
            <div className="p-5 space-y-3">
              <p className="text-xs text-zinc-400">
                {preview.summary.days_generated} Tage werden in der Zeiterfassung des Fahrers gespeichert. Bitte einmalig signieren — die Unterschrift wird automatisch auf jeden Eintrag dieses Laufs übernommen.
              </p>
              <SignatureCanvas
                value={sigDataUrl}
                onChange={setSigDataUrl}
                label="Unterschrift Fahrer"
                testid="autogen-signature-canvas"
              />
              <p className="text-[11px] text-zinc-500">
                Hinweis: Falls keine Unterschrift gezeichnet wird, nutzt das System die in der Mitarbeiter-Karte hinterlegte Standard-Unterschrift (falls vorhanden).
              </p>
            </div>
            <footer className="px-5 py-3 border-t border-zinc-800 flex justify-end gap-2">
              <button onClick={() => setSigModalOpen(false)} disabled={committing}
                data-testid="autogen-sig-cancel"
                className="px-4 py-2 text-sm text-zinc-400 hover:text-zinc-200">Abbrechen</button>
              <button onClick={commitWithSignature} disabled={committing}
                data-testid="autogen-sig-confirm"
                className="px-4 py-2 text-sm bg-amber-600 hover:bg-amber-700 disabled:opacity-30 text-white rounded font-medium">
                {committing ? "Speichere …" : `${preview.summary.days_generated} Einträge mit Unterschrift speichern`}
              </button>
            </footer>
          </div>
        </div>
      )}
    </div>
  );
}

const INP = "w-full px-3 py-2 bg-zinc-950 border border-zinc-800 rounded text-sm text-zinc-100 focus:border-amber-500/60 outline-none";

function Field({ label, icon: Icon, children }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold mb-1.5 flex items-center gap-1.5">
        {Icon && <Icon className="w-3 h-3"/>} {label}
      </label>
      {children}
    </div>
  );
}

function PreviewSection({ preview, onRegenDay, onRemoveDay, onCommit, committing }) {
  const { draft, summary, issues } = preview;
  const errors = (issues || []).filter(i => i.severity === "error");
  const warnings = (issues || []).filter(i => i.severity === "warning");
  return (
    <section className="bg-zinc-900 border border-zinc-800 rounded-lg overflow-hidden" data-testid="autogen-preview">
      <header className="px-5 py-4 border-b border-zinc-800 flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-wider text-zinc-500">Monatsvorschau</div>
          <h2 className="font-display font-semibold text-zinc-50">
            {draft.employee_name} · {draft.year}/{String(draft.month).padStart(2,"0")} · {draft.rule_code}
          </h2>
        </div>
        <div className="flex items-center gap-4 text-xs">
          <Stat label="Generiert" value={summary.days_generated} tone="emerald"/>
          <Stat label="Geskipt" value={summary.days_skipped} tone="zinc"/>
          {summary.days_with_existing_entry > 0 && <Stat label="Überschreibt" value={summary.days_with_existing_entry} tone="amber"/>}
          {summary.overtime.target_minutes > 0 && (
            <Stat label="Überstunden" value={`${Math.round(summary.overtime.distributed_minutes/60*10)/10}h / ${Math.round(summary.overtime.target_minutes/60*10)/10}h`} tone="blue"/>
          )}
        </div>
      </header>

      {(errors.length > 0 || warnings.length > 0) && (
        <div className="px-5 py-3 bg-amber-500/5 border-b border-amber-500/20 text-xs text-amber-200 flex items-center gap-2">
          <AlertTriangle className="w-3.5 h-3.5"/>
          {errors.length} Fehler · {warnings.length} Warnungen — bitte vor Speichern prüfen
        </div>
      )}

      {preview.signature && !preview.signature.has_signature && (
        <div className="px-5 py-3 bg-orange-500/5 border-b border-orange-500/30 text-xs text-orange-200 flex items-center gap-2" data-testid="autogen-no-signature-warning">
          <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0"/>
          Keine Signatur für {draft.employee_name} hinterlegt — die Einträge werden ohne Unterschrift gespeichert.
          {" "}Tipp: Im Mitarbeiter-Profil → Portal-Zugang einmalig erfassen.
        </div>
      )}

      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="autogen-preview-table">
          <thead className="bg-zinc-950/60 text-[10px] uppercase tracking-wider text-zinc-500">
            <tr>
              <th className="text-left px-3 py-2">Datum</th>
              <th className="text-left px-3 py-2">Tag</th>
              <th className="text-left px-3 py-2">Arbeit</th>
              <th className="text-left px-3 py-2">Lenkzeit</th>
              <th className="text-left px-3 py-2">Pausen</th>
              <th className="text-right px-3 py-2">Start-KM</th>
              <th className="text-right px-3 py-2">End-KM</th>
              <th className="text-right px-3 py-2">ÜZ</th>
              <th className="text-right px-3 py-2">Aktionen</th>
            </tr>
          </thead>
          <tbody>
            {draft.days.map(d => <DayRow key={d.date} d={d} onRegen={() => onRegenDay(d)} onRemove={() => onRemoveDay(d)}/>)}
          </tbody>
        </table>
      </div>

      <footer className="px-5 py-4 border-t border-zinc-800 flex items-center justify-between gap-3 flex-wrap">
        <p className="text-[11px] text-zinc-500">
          Run-ID: <span className="font-mono">{draft.run_id.slice(0, 8)}…</span> · Status: Generiert
        </p>
        <button onClick={onCommit} disabled={committing || errors.length > 0}
          data-testid="autogen-commit-btn"
          className="inline-flex items-center gap-2 px-5 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded font-medium disabled:opacity-40">
          <Save className="w-4 h-4"/>
          {committing ? "Speichere …" : `${summary.days_generated} Einträge in Zeiterfassung speichern`}
        </button>
      </footer>
    </section>
  );
}

function DayRow({ d, onRegen, onRemove }) {
  const wdName = ["Mo","Di","Mi","Do","Fr","Sa","So"][d.weekday];
  if (d.skipped) {
    return (
      <tr className="border-b border-zinc-800/40 bg-zinc-950/40 opacity-50">
        <td className="px-3 py-2 font-mono text-xs text-zinc-500">{fmtDate(d.date)}</td>
        <td className="px-3 py-2 text-xs text-zinc-500">{wdName}</td>
        <td colSpan={6} className="px-3 py-2 text-xs italic text-zinc-500">— {d.skip_reason} —</td>
        <td className="px-3 py-2 text-right">
          {!d.existing_entry_id && (
            <button onClick={onRegen} title="Generieren" data-testid={`autogen-regen-${d.date}`}
              className="text-amber-400 hover:text-amber-300"><RotateCcw className="w-3.5 h-3.5"/></button>
          )}
        </td>
      </tr>
    );
  }
  return (
    <tr className="border-b border-zinc-800/40 hover:bg-zinc-800/30" data-testid={`autogen-day-${d.date}`}>
      <td className="px-3 py-2 font-mono text-xs text-zinc-300">{fmtDate(d.date)}</td>
      <td className="px-3 py-2 text-xs text-zinc-400">{wdName}</td>
      <td className="px-3 py-2 font-mono text-xs text-zinc-100">{d.work_start} – {d.work_end}</td>
      <td className="px-3 py-2 font-mono text-xs text-zinc-300">{d.driving_start} – {d.driving_end}</td>
      <td className="px-3 py-2 text-[11px] text-zinc-400">
        {(d.pauses || []).map(p => (
          <span key={p.label} className="inline-block mr-1.5">
            <span className="text-zinc-500">{p.label}</span> {p.start}-{p.end}
          </span>
        ))}
      </td>
      <td className="px-3 py-2 font-mono text-xs text-zinc-300 text-right tabular-nums">{d.start_km?.toLocaleString("de-AT")}</td>
      <td className="px-3 py-2 font-mono text-xs text-zinc-100 text-right tabular-nums">{d.end_km?.toLocaleString("de-AT")}</td>
      <td className="px-3 py-2 text-xs text-right">
        {d.overtime_added > 0 ? <span className="text-amber-400 font-medium">+{d.overtime_added}m</span> : <span className="text-zinc-600">—</span>}
      </td>
      <td className="px-3 py-2 text-right">
        <button onClick={onRegen} title="Neu generieren" data-testid={`autogen-regen-${d.date}`}
          className="text-amber-400 hover:text-amber-300 mr-2"><RotateCcw className="w-3.5 h-3.5"/></button>
        <button onClick={onRemove} title="Tag entfernen" data-testid={`autogen-remove-${d.date}`}
          className="text-red-400 hover:text-red-300"><X className="w-3.5 h-3.5"/></button>
      </td>
    </tr>
  );
}

function Stat({ label, value, tone }) {
  const map = {
    emerald: "text-emerald-300", amber: "text-amber-300",
    blue: "text-blue-300", zinc: "text-zinc-400",
  };
  return (
    <div>
      <div className="text-[9px] uppercase tracking-wider text-zinc-500">{label}</div>
      <div className={`text-base font-bold tabular-nums ${map[tone]}`}>{value}</div>
    </div>
  );
}
